/**
 * Booking Card Component
 * Displays a single booking with status and actions
 */

import type { Booking } from "@common/types";
import { formatBookingDate, bookingTimeZone } from "../utils/booking-format";
import { useLocaleStore } from "../stores/locale-store";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatClock } from "@common/booking-overlap";
import { slotLabel, currentClockLocale } from "../utils/slot-availability";
import { getBookingStatusLabel, getBookingTimes } from "../utils/booking";

// Business timezone - all times are displayed in this timezone


interface BookingCardProps {
  booking: Booking;
  onViewDetails?: () => void;
  onUpdateStatus?: (status: string, invoiceNumber?: string) => void;
  onCreateInvoice?: () => void; // Triggers invoice modal
  onCancelInvoice?: () => void; // Cancel invoice and revert to confirmed
  onGeneratePaymentLink?: () => void; // Triggers Stripe payment link modal
  onCancel?: () => void;
  onRestore?: () => void;
  onEditDate?: () => void;
  compact?: boolean;
  showActions?: boolean;
  canCancel?: boolean;
  isAdmin?: boolean; // Controls visibility of admin-only actions (invoice buttons, status updates)
}

/** Wide screens show a booking in full; on a phone the details fold under a one-line summary. */
function useWideScreen(): boolean {
  const query = "(min-width: 640px)";
  const [wide, setWide] = useState(() => typeof window === "undefined" || typeof window.matchMedia !== "function" || window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const on = () => setWide(media.matches);
    media.addEventListener?.("change", on);
    return () => media.removeEventListener?.("change", on);
  }, []);
  return wide;
}

export default function BookingCard({
  booking,
  onViewDetails,
  onUpdateStatus,
  onCreateInvoice,
  onCancelInvoice,
  onGeneratePaymentLink,
  onCancel,
  onRestore,
  onEditDate,
  compact = false,
  showActions = false,
  canCancel = false,
  isAdmin = false,
}: BookingCardProps) {
  const { t } = useTranslation();
  const language = useLocaleStore(state => state.language);
  const timeZone = bookingTimeZone(booking.date);
  const wide = useWideScreen();

  const getStatusBadge = (status: string) => {
    const classes: Record<string, string> = {
      pending: "badge-pending",
      confirmed: "badge-confirmed",
      cancelled: "badge-cancelled",
      completed: "badge-completed",
      invoiced: "badge-invoiced",
    };
    return classes[status] || "badge";
  };

  // Format time slot for display
  const formatTimeSlot = (timeSlot?: string) => (timeSlot ? slotLabel(timeSlot, t) : "");

  // Get display time (custom or time slot)
  const displayTime = booking.customStartTime || booking.timeSlot;

  // End time: start plus the custom or trip duration
  const rawEnd = getBookingTimes(booking).end;
  const endTime = rawEnd ? formatClock(rawEnd, currentClockLocale()) : null;

  return (
    <div className="card min-w-0 [overflow-wrap:anywhere]">
      {/* Header with confirmation code and status */}
      <div className="flex justify-between items-start gap-2 mb-3">
        <div className="flex min-w-0 items-center flex-wrap gap-2">
          <span className="font-mono text-sm text-gray-500">
            {booking.confirmationCode}
          </span>
          <span className={getStatusBadge(booking.status)}>
            {getBookingStatusLabel(booking, status => t(`partnerPages.status.${status}`, { defaultValue: status }))}
          </span>
          {booking.invoiceNumber && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
              📄 {booking.invoiceNumber}
            </span>
          )}
          {booking.stripePaymentStatus === "pending" && (
            <>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                💳 {t("bookingCard.stripe.pending")}
              </span>
              <a
                href={`/pay/${booking.confirmationCode}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100"
                title={t("bookingCard.stripe.openPaymentPage")}
              >
                🔗 {t("bookingCard.stripe.paymentLink")}
              </a>
            </>
          )}
          {booking.stripePaymentStatus === "completed" && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
              💳 {t("bookingCard.stripe.completed")}
            </span>
          )}
          {booking.stripePaymentStatus === "expired" && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              💳 {t("bookingCard.stripe.expired")}
            </span>
          )}
          {booking.stripePaymentStatus === "refunded" && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
              💳 {t("bookingCard.stripe.refunded")}
            </span>
          )}
          {booking.restoredAt && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
              ↩️ {t("bookingCard.restored")}
            </span>
          )}
        </div>
        {booking.numberOfGuests > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            👥 {t("bookingCard.guestCount", { count: booking.numberOfGuests })}
          </span>
        )}
      </div>

      {/* Trip and Date Info */}
      <div className="mb-4">
        <h3 className="font-semibold text-sea-900 text-lg [overflow-wrap:anywhere]">
          {booking.tripName || t("bookingCard.defaultTripName")}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          📅 {formatBookingDate(booking.date, language)}
          {displayTime && (
            <span
              className="ml-2"
              title={t("bookingCard.localTimeTitle", { tz: timeZone })}
            >
              🕐{" "}
              {formatTimeSlot(displayTime)}
              {endTime && <span className="text-gray-500"> → {endTime}</span>}
              <span className="text-xs text-gray-400 ml-1">
                {timeZone}
              </span>
              {booking.customStartTime && (
                <span className="text-xs text-amber-600 ml-1">{t("bookingCard.customTime")}</span>
              )}
            </span>
          )}
        </p>
      </div>

      <details key={compact ? "compact" : "expanded"} open={wide && !compact ? true : undefined} className="group">
      <summary className={`${compact ? "" : "sm:hidden"} mb-3 min-h-11 cursor-pointer list-none rounded-lg bg-gray-50 p-3 text-sm text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sea-700`}>
        <span className="font-medium text-gray-900">{booking.customer.name}</span>
        {booking.partnerName ? ` · ${booking.partnerName}` : ""}
        <span className="ml-2 text-sea-700 underline">{t(compact ? "sitePolish.bookingDetails" : "partnerPages.dashboard.guestDetails")}</span>
      </summary>
      {/* Partner and Guest Info - Clear separation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg mb-3">
        {/* Booked By (Partner) */}
        <div className="border-r-0 md:border-r border-gray-200 pr-0 md:pr-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t("bookingCard.bookedBy")}
          </p>
          <p className="font-medium text-purple-700">
            🏢 {booking.partnerName || t("bookingCard.unknownPartner")}
          </p>
          {booking.userName && (
            <p className="text-sm text-gray-600 mt-0.5">
              👤 {booking.userName}
              {booking.userEmail && (
                <span className="text-gray-400 text-xs ml-1">
                  ({booking.userEmail})
                </span>
              )}
            </p>
          )}
          {booking.departure?.name && (
            <p className="text-sm text-gray-600 mt-1">
              📍 {t("bookingCard.departure")}{" "}
              <span className="font-medium">{booking.departure.name}</span>
              {booking.departure.address && <span className="text-gray-500">, {booking.departure.address}</span>}
              {booking.departure.mapUrl && (
                <>
                  {" "}
                  <a
                    href={booking.departure.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sea-600 underline"
                  >
                    {t("bookingCard.map")}
                  </a>
                </>
              )}
            </p>
          )}
          {booking.bookingReference && (
            <p className="text-sm text-blue-600 mt-1">
              🔖 {t("bookingCard.ref")}{" "}
              <span className="font-medium">{booking.bookingReference}</span>
            </p>
          )}
        </div>

        {/* Guest Info */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {t("bookingCard.guest")}
          </p>
          <p className="font-medium text-gray-900">
            🎫 {booking.customer.name}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            🌍 {booking.customer.country} • 📱 {booking.customer.mobile}
          </p>
          {booking.customer.email && (
            <p className="text-sm text-gray-500">✉️ {booking.customer.email}</p>
          )}
        </div>
      </div>

      {/* Notes */}
      {booking.notes && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mb-3">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">📝 {t("bookingCard.notes")}</span> {booking.notes}
          </p>
        </div>
      )}

      {/* Price Summary */}
      {booking.tripPrice !== undefined && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-gray-600">
              {t("bookingCard.price.trip")}{" "}
              <span className="font-medium text-gray-900">
                {booking.tripPrice} {booking.tripCurrency}
              </span>
            </span>
            {booking.includeLunch &&
              booking.totalLunchPrice !== undefined &&
              booking.totalLunchPrice > 0 && (
                <span className="text-gray-600">
                  🍽️ {t("bookingCard.price.lunch")}{" "}
                  <span className="font-medium text-gray-900">
                    {booking.totalLunchPrice} {booking.tripCurrency}
                  </span>
                  <span className="text-xs text-gray-400 ml-1">
                    ({booking.numberOfGuests} × {booking.lunchPricePerGuest})
                  </span>
                </span>
              )}
            <span className="text-sea-700 font-semibold">
              {t("bookingCard.price.total")}{" "}
              {booking.tripPrice +
                (booking.totalLunchPrice || 0) +
                (booking.extraCost || 0) +
                (booking.articles?.reduce(
                  (sum, a) => sum + a.unitPrice * a.quantity,
                  0,
                ) || 0)}{" "}
              {booking.tripCurrency}
              <span className="text-xs text-gray-400 font-normal ml-1">
                {t("bookingCard.price.exclVat")}
              </span>
            </span>
            {booking.fortnoxInvoiceTotal !== undefined && (
              <span className="text-gray-700 font-semibold">
                {booking.fortnoxInvoiceTotal} {booking.tripCurrency}
                <span className="text-xs text-gray-400 font-normal ml-1">
                  {t("bookingCard.price.inclVat")}
                </span>
              </span>
            )}
          </div>
          {/* Extra cost on separate line for clarity */}
          {booking.extraCost !== undefined && booking.extraCost > 0 && (
            <div className="mt-2 text-sm">
              <span className="text-gray-600">
                💰 {t("bookingCard.price.extraCost")}{" "}
                <span className="font-medium text-gray-900">
                  {booking.extraCost} {booking.tripCurrency}
                </span>
              </span>
              {booking.extraCostDescription && (
                <span className="text-gray-500 ml-2">
                  — {booking.extraCostDescription}
                </span>
              )}
            </div>
          )}
          {/* Additional articles */}
          {booking.articles && booking.articles.length > 0 && (
            <div className="mt-2 text-sm">
              <span className="text-gray-600 font-medium">
                📦 {t("bookingCard.price.additionalArticles")}
              </span>
              <div className="ml-4 mt-1 space-y-1">
                {booking.articles.map((article, index) => (
                  <div
                    key={index}
                    className="flex justify-between text-gray-600"
                  >
                    <span>
                      {article.description} × {article.quantity}
                    </span>
                    <span className="font-medium text-gray-900">
                      {(article.unitPrice * article.quantity).toFixed(0)}{" "}
                      {booking.tripCurrency}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </details>

      {(showActions || canCancel) && (
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-2 sm:justify-end">
          {booking.status !== "cancelled" && (
            <Link
              to={`/bookings/${encodeURIComponent(booking.id)}/print?date=${booking.date}`}
              className="btn-secondary min-h-11 text-sm"
            >
              {t("bookingPdf.pdf")}
            </Link>
          )}
          {onViewDetails && (
            <button onClick={onViewDetails} className="btn-secondary min-h-11 text-sm">
              {t("bookingCard.actions.viewDetails")}
            </button>
          )}

          {/* Edit button - only for pending, confirmed, and invoiced bookings */}
          {showActions &&
            onEditDate &&
            (booking.status === "pending" ||
              booking.status === "confirmed" ||
              booking.status === "invoiced") && (
              <button onClick={onEditDate} className="btn-secondary min-h-11 text-sm">
                {t("bookingCard.actions.edit")}
              </button>
            )}

          {/* Admin actions for pending bookings */}
          {isAdmin && onUpdateStatus && booking.status === "pending" && (
            <>
              <button
                onClick={() => onUpdateStatus("confirmed")}
                className="btn-primary min-h-11 text-sm"
              >
                {t("bookingCard.actions.confirm")}
              </button>
            </>
          )}

          {/* Admin action to mark confirmed as invoiced */}
          {isAdmin &&
            onCreateInvoice &&
            booking.status === "confirmed" &&
            !booking.invoiceNumber && (
              <button
                onClick={onCreateInvoice}
                className="inline-flex items-center px-3 min-h-11 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                {t("bookingCard.actions.createInvoice")}
              </button>
            )}

          {/* Admin actions for invoiced bookings */}
          {isAdmin && booking.status === "invoiced" && (
            <>
              {/* Generate Stripe payment link — show when no pending link, or when expired */}
              {onGeneratePaymentLink &&
                booking.fortnoxInvoiceNumber &&
                (!booking.stripePaymentStatus ||
                  booking.stripePaymentStatus === "expired") && (
                  <button
                    onClick={onGeneratePaymentLink}
                    className="inline-flex items-center px-3 min-h-11 py-2 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
                    title={t("bookingCard.actions.generatePaymentLinkTitle")}
                  >
                    💳 {t("bookingCard.actions.generatePaymentLink")}
                  </button>
                )}
              {onUpdateStatus && (
                <button
                  onClick={() => onUpdateStatus("completed")}
                  className="btn-primary min-h-11 text-sm"
                >
                  {t("bookingCard.actions.markComplete")}
                </button>
              )}
              {booking.stripePaymentStatus === "pending" ? (
                onCancel && (
                  <button
                    onClick={onCancel}
                    className="inline-flex items-center px-3 min-h-11 py-2 text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                    title={t("bookingCard.actions.cancelPaymentLinkTitle")}
                  >
                    {t("bookingCard.actions.cancelPaymentLink")}
                  </button>
                )
              ) : booking.stripePaymentStatus === "completed" ||
                booking.stripePaymentStatus === "refunded" ? (
                <span
                  className="inline-flex items-center px-3 min-h-11 py-2 text-sm font-medium rounded-md text-amber-700 bg-amber-50"
                  title={t("bookingCard.actions.creditInvoiceRequiredTitle")}
                >
                  {t("bookingCard.actions.creditInvoiceRequired")}
                </span>
              ) : (
                onCancelInvoice && !booking.fortnoxInvoiceSent && (
                  <button
                    onClick={onCancelInvoice}
                    className="inline-flex items-center px-3 min-h-11 py-2 text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                    title={t("bookingCard.actions.cancelInvoiceTitle")}
                  >
                    {t("bookingCard.actions.cancelInvoice")}
                  </button>
                )
              )}
            </>
          )}

          {/* Restore button - admin only, for cancelled bookings */}
          {isAdmin && onRestore && booking.status === "cancelled" && (
            <button
              onClick={onRestore}
              className="inline-flex items-center px-3 min-h-11 py-2 text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
            >
              ↩️ {t("bookingCard.actions.restoreBooking")}
            </button>
          )}

          {/* Cancel button - available for both admin and partners (on their own bookings) */}
          {canCancel &&
            onCancel &&
            (booking.status === "pending" ||
              booking.status === "confirmed") && (
              <button onClick={onCancel} className="btn bg-white text-red-700 border-red-300 hover:bg-red-50 text-xs order-last basis-full sm:basis-auto sm:ml-4">
                {t("bookingCard.actions.cancelBooking")}
              </button>
            )}

          {/* Refund & cancel — admin only, for Stripe-paid completed bookings */}
          {isAdmin &&
            onCancel &&
            booking.status === "completed" &&
            booking.stripePaymentStatus === "completed" && (
              <button onClick={onCancel} className="btn-danger text-xs">
                {t("bookingCard.actions.refundAndCancel")}
              </button>
            )}
        </div>
      )}
    </div>
  );
}
